import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Frame } from "@volca/ui/components/ui/frame";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import { count } from "@volca/ui/trades";

/** A run of a cell's text, and where it links when the reply linked it. */
interface Part {
  text: string;
  href?: string;
}

interface Cell {
  text: string;
  parts: Part[];
  /** Written in bold, as a total row is. */
  strong: boolean;
}

/** A table from a reply, read off its rendered markup when it opens. */
export interface Grid {
  /** The sentence or heading that leads into it in the reply. */
  lead: string;
  columns: { name: string; align: "left" | "right" | "center" }[];
  rows: Cell[][];
}

// $1,204.50, -242.9%, (12), 3.5x, 1,024 hrs: a figure, read down its column.
const FIGURE =
  /^[−\-+]?\(?[−\-+]?[$€£]?\s?\d[\d,]*(\.\d+)?\)?\s?(%|x|[a-z]{1,3})?$/i;
// Job #, Invoice ID: whole numbers that name a record, read as text.
const IDENTIFIER = /#$|\b(id|no|number|code)\b/i;
const LEAD_LENGTH = 160;

// The cell's text in order, its links kept (their addresses as the reply's
// markdown already made safe), so a table opened whole still links out.
const partsOf = (node: Node): Part[] =>
  [...node.childNodes].flatMap((child): Part[] => {
    if (child instanceof HTMLAnchorElement && child.getAttribute("href"))
      return [
        {
          text: child.textContent ?? "",
          href: child.getAttribute("href") ?? "",
        },
      ];
    if (child.nodeType === Node.TEXT_NODE)
      return [{ text: child.textContent ?? "" }];
    return partsOf(child);
  });

const cellOf = (cell: Element): Cell => {
  const text = cell.textContent?.trim() ?? "";
  const strong = cell.querySelector("strong, b");
  return {
    text,
    parts: partsOf(cell),
    strong: !!text && strong?.textContent?.trim() === text,
  };
};

const CellText = ({ cell }: { cell: Cell | undefined }) =>
  cell?.parts.some((part) => part.href)
    ? cell.parts.map((part, index) =>
        part.href ? (
          <a
            key={index}
            href={part.href}
            className="text-link underline-offset-2 hover:underline"
          >
            {part.text}
          </a>
        ) : (
          part.text
        ),
      )
    : cell?.text;

const leadOf = (element: Element | null) => {
  const text = element?.textContent?.trim() ?? "";
  if (!element || !text || text.length > LEAD_LENGTH) return "";
  if (/^H[1-6]$/.test(element.tagName)) return text;
  return element.tagName === "P" && text.endsWith(":")
    ? `${text.slice(0, -1)}.`
    : "";
};

/**
 * The table as data: its columns (aligned as written, else a column of
 * figures, not identifiers, to the right), its rows' cells, and what the
 * reply says before it.
 */
export const readGrid = (table: HTMLTableElement, before: Element | null) => {
  const heads = [...table.querySelectorAll("thead th")];
  const rows = [...table.querySelectorAll("tbody tr")].map((row) =>
    [...row.querySelectorAll("td, th")].map(cellOf),
  );
  const columns = heads.map((head, index) => {
    const written =
      head.getAttribute("align") ?? (head as HTMLElement).style.textAlign;
    const filled = rows.map((row) => row[index]?.text).filter(Boolean);
    const name = head.textContent?.trim() ?? "";
    const figures =
      filled.length > 0 &&
      filled.every((t) => FIGURE.test(t)) &&
      !(IDENTIFIER.test(name) && filled.every((t) => /^\d+$/.test(t)));
    const align: Grid["columns"][number]["align"] =
      written === "right" || written === "center"
        ? written
        : figures
          ? "right"
          : "left";
    return { name, align };
  });
  return { lead: leadOf(before), columns, rows } satisfies Grid;
};

const alignClass = (align: Grid["columns"][number]["align"]) => ({
  "text-right tabular-nums": align === "right",
  "text-center": align === "center",
});

/**
 * A reply's table opened whole: on a wide screen, a data table in a frame,
 * its header held while the rows scroll, figures right-aligned; on a phone,
 * a record a row (its first value as the heading, the rest under their
 * columns' names), so nothing scrolls sideways.
 */
export const ChatTableDialog = ({
  grid,
  open,
  onOpenChange,
  container,
}: {
  grid: Grid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A native modal `<dialog>` the table sits in, to open above it. */
  container: HTMLElement | undefined;
}) => {
  const { lead, columns, rows } = grid;
  const shape = `${count(rows.length, "row")} · ${count(columns.length, "column")}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        container={container}
        className="@container max-h-[85vh] grid-rows-[auto_minmax(0,1fr)] sm:max-w-6xl"
      >
        <DialogHeader>
          <DialogTitle>Table</DialogTitle>
          <DialogDescription>{lead || shape}</DialogDescription>
        </DialogHeader>
        <Frame spacing="xs" className="hidden min-h-0 @2xl:flex">
          <Table variant="card" scroll aria-label={lead || "Table"}>
            <TableHeader>
              <TableRow>
                {columns.map((column, index) => (
                  <TableHead
                    key={index}
                    scope="col"
                    className={cn(alignClass(column.align))}
                  >
                    {column.name}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((cells, row) => (
                <TableRow key={row}>
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      className={cn(
                        "align-top",
                        alignClass(column.align),
                        column.align === "left" &&
                          "max-w-96 min-w-32 [overflow-wrap:anywhere] whitespace-normal",
                        { "font-medium text-ink": cells[index]?.strong },
                      )}
                    >
                      <CellText cell={cells[index]} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {lead && (
            <p className="px-2 py-1 text-xs text-subtle tabular-nums">
              {shape}
            </p>
          )}
        </Frame>
        <ol
          aria-label={lead || "Table"}
          tabIndex={0}
          className="-mx-1 min-h-0 space-y-2 overflow-y-auto overscroll-contain rounded-sm px-1 py-0.5 outline-none focus-visible:ring-3 focus-visible:ring-focus/50 @2xl:hidden"
        >
          {rows.map(([first, ...rest], row) => (
            <li
              key={row}
              className="rounded-lg border border-line bg-surface-raised px-3 py-2.5"
            >
              <p className="font-medium [overflow-wrap:anywhere] text-ink">
                {first?.text ? <CellText cell={first} /> : "—"}
              </p>
              <dl className="mt-1.5 space-y-1">
                {rest.map((cell, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-3"
                  >
                    <dt className="text-subtle">
                      {columns[index + 1]?.name || "—"}
                    </dt>
                    <dd
                      className={cn("[overflow-wrap:anywhere]", {
                        "tabular-nums": columns[index + 1]?.align === "right",
                        "font-medium": cell.strong,
                      })}
                    >
                      {cell.text ? <CellText cell={cell} /> : "—"}
                    </dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
};
